import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import CreditMeter from '@/components/CreditMeter';
import UserMenu from '@/components/UserMenu';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import {
  Coins, Sparkles, Image, Paintbrush, Layout, Wand2,
  Globe, MessageSquare, Zap, Plus, Check, Minus, Crown, ExternalLink, CheckCircle,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Action metadata for costs + history ── */
const ACTION_META = {
  build:          { icon: Sparkles, label: 'Build Website',       color: 'var(--orbis-blue)' },
  design_system:  { icon: Paintbrush, label: 'Design System',     color: '#A78BFA' },
  structure:      { icon: Layout, label: 'Structure',             color: '#34D399' },
  image_generate: { icon: Image, label: 'Image Generation',       color: '#F59E0B' },
  element_edit:   { icon: Wand2, label: 'Element Edit',           color: '#60A5FA' },
  global_edit:    { icon: Paintbrush, label: 'Global Edit',       color: 'var(--orbis-t3)' },
  clone_analyze:  { icon: Globe, label: 'Clone Analysis',         color: 'var(--orbis-amber)' },
  clone_build:    { icon: Sparkles, label: 'Clone Build',         color: 'var(--orbis-amber)' },
  clone_chat:     { icon: MessageSquare, label: 'Clone Chat',     color: 'var(--orbis-amber)' },
  purchase:       { icon: Plus, label: 'Credits Added',           color: 'var(--orbis-positive)' },
  tier_upgrade:   { icon: Crown, label: 'Plan Upgrade',           color: 'var(--orbis-blue)' },
  monthly_reset:  { icon: Zap, label: 'Monthly Reset',            color: 'var(--orbis-positive)' },
};

const TIER_COLORS = {
  free: 'var(--orbis-t4)',
  pro: 'var(--orbis-blue)',
  enterprise: 'var(--orbis-amber)',
};

/* ── Plans ── */
const PLANS = [
  {
    id: 'free', name: 'Free',
    description: 'Discover what Orbis can do for you',
    monthlyPrice: 0, annualPrice: 0,
    cta: 'Get Started', highlight: false,
    creditOptions: null, defaultCredits: 30,
    featureHeader: 'Free for everyone',
    features: ['5 daily credits (up to 30/month)', 'Public projects', 'Landing pages only', 'Standard AI models', 'Unlimited collaborators'],
  },
  {
    id: 'pro', name: 'Pro',
    description: 'Designed for fast-moving teams building together in real time',
    monthlyPrice: 25, annualPrice: 20,
    cta: 'Get Started', highlight: true, badge: 'Most Popular',
    creditOptions: [
      { credits: 100, monthlyPrice: 25, annualPrice: 20 },
      { credits: 200, monthlyPrice: 45, annualPrice: 36 },
      { credits: 500, monthlyPrice: 99, annualPrice: 79 },
    ],
    defaultCredits: 100,
    featureHeader: 'Everything in Free, plus:',
    features: ['{credits} monthly credits', '5 daily credits (up to 150/month)', 'Credit rollovers', 'Custom domains', 'Remove Orbis badge', 'Private projects', 'User roles & permissions'],
  },
  {
    id: 'business', name: 'Business',
    description: 'Advanced controls and power features for growing departments',
    monthlyPrice: 50, annualPrice: 42,
    cta: 'Get Started', highlight: false,
    creditOptions: [
      { credits: 100, monthlyPrice: 50, annualPrice: 42 },
      { credits: 200, monthlyPrice: 80, annualPrice: 67 },
      { credits: 500, monthlyPrice: 175, annualPrice: 146 },
    ],
    defaultCredits: 100,
    featureHeader: 'All features in Pro, plus:',
    features: ['{credits} monthly credits', 'SSO authentication', 'Personal projects', 'Opt out of data training', 'Design templates', 'Advanced AI models', 'Priority support'],
  },
  {
    id: 'enterprise', name: 'Enterprise',
    description: 'Built for large orgs needing flexibility, scale, and governance',
    monthlyPrice: null, annualPrice: null,
    cta: 'Book a Demo', highlight: false,
    creditOptions: null, defaultCredits: null,
    featureHeader: 'Everything in Business, plus:',
    features: ['Dedicated support', 'Onboarding services', 'Custom connections', 'Group-based access control', 'Custom design systems', 'SLA & security audits', 'Flexible billing'],
  },
];

const COMPARE_ROWS = [
  { category: 'Usage' },
  { label: 'Monthly Credits',     free: '30',       pro: '100 – 500',  business: '100 – 500',  enterprise: 'Custom' },
  { label: 'Daily Credits',       free: '5',        pro: '5',          business: '5',          enterprise: 'Unlimited' },
  { label: 'AI Models',           free: 'Standard', pro: 'Advanced',   business: 'Premium',    enterprise: 'Premium+' },
  { label: 'Image Quality',       free: 'Basic',    pro: 'High',       business: 'Ultra',      enterprise: 'Ultra' },
  { category: 'Limits' },
  { label: 'Projects',            free: '3',        pro: '10',         business: '25',         enterprise: 'Unlimited' },
  { label: 'Pages per Project',   free: '1',        pro: '10',         business: 'Unlimited',  enterprise: 'Unlimited' },
  { label: 'Team Members',        free: 'Unlimited',pro: 'Unlimited',  business: 'Unlimited',  enterprise: 'Unlimited' },
  { category: 'Features' },
  { label: 'Clone Mode',          free: true,       pro: true,         business: true,         enterprise: true },
  { label: 'Custom Domain',       free: false,      pro: true,         business: true,         enterprise: true },
  { label: 'Private Projects',    free: false,      pro: true,         business: true,         enterprise: true },
  { label: 'SSO',                 free: false,      pro: false,        business: true,         enterprise: true },
  { label: 'API Access',          free: false,      pro: false,        business: true,         enterprise: true },
  { label: 'GitHub Export',       free: false,      pro: true,         business: true,         enterprise: true },
  { label: 'Design Templates',    free: false,      pro: false,        business: true,         enterprise: true },
  { label: 'Credit Rollovers',    free: false,      pro: true,         business: true,         enterprise: true },
  { label: 'Remove Orbis Badge',  free: false,      pro: true,         business: true,         enterprise: true },
  { label: 'Version History',     free: '5',        pro: '20',         business: 'Unlimited',  enterprise: 'Unlimited' },
  { label: 'Priority Support',    free: false,      pro: false,        business: true,         enterprise: true },
  { label: 'Dedicated Support',   free: false,      pro: false,        business: false,        enterprise: true },
  { label: 'Custom Design Systems',free: false,     pro: false,        business: false,        enterprise: true },
];

const CREDIT_PACKS = [
  { amount: 100, price: 'Free during beta' },
  { amount: 500, price: 'Free during beta' },
  { amount: 1000, price: 'Free during beta' },
];

/* ── Animation variants ── */
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12 + 0.3, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const fadeIn = {
  hidden: { opacity: 0, y: -15 },
  visible: (d = 0) => ({ opacity: 1, y: 0, transition: { delay: d, duration: 0.6, ease: 'easeOut' } }),
};


export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { balance, tier, monthlyCredits, lifetimeUsed, costs, refresh, loading } = useCredits();
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [upgrading, setUpgrading] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [selectedCredits, setSelectedCredits] = useState({});
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success(`Upgraded to ${searchParams.get('tier')?.toUpperCase() || 'new plan'}!`);
      refresh();
      navigate('/billing', { replace: true });
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Checkout canceled');
      navigate('/billing', { replace: true });
    }
  }, [searchParams, refresh, navigate]);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try { const resp = await axios.get(`${API_URL}/credits`); setHistory(resp.data.history || []); }
    catch { /* silent */ } finally { setHistoryLoading(false); }
  };

  const handleUpgrade = async (planId) => {
    if (planId === tier || planId === 'free') return;
    setUpgrading(planId);
    try {
      const resp = await axios.post(`${API_URL}/credits/checkout`, { tier: planId });
      if (resp.data.checkout_url) window.location.href = resp.data.checkout_url;
      else if (resp.data.direct_upgrade) {
        toast.success(`Upgraded to ${planId.toUpperCase()}!`);
        await refresh(); await fetchHistory();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upgrade failed'); }
    finally { setUpgrading(null); }
  };

  const handleManageSubscription = async () => {
    try { const resp = await axios.post(`${API_URL}/credits/portal`); if (resp.data.portal_url) window.location.href = resp.data.portal_url; }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not open subscription manager'); }
  };

  const handlePurchase = async (amount) => {
    setPurchasing(amount);
    try { await axios.post(`${API_URL}/credits/purchase`, { amount }); toast.success(`Added ${amount} credits`); await refresh(); await fetchHistory(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Purchase failed'); }
    finally { setPurchasing(null); }
  };

  const tierColor = TIER_COLORS[tier] || 'var(--orbis-t4)';
  const tierLabel = tier?.toUpperCase() || 'FREE';
  const usagePercent = monthlyCredits > 0 ? Math.min(100, ((monthlyCredits - balance) / monthlyCredits) * 100) : 0;
  const tierOrder = ['free', 'pro', 'business', 'enterprise'];
  const currentTierIdx = tierOrder.indexOf(tier);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--surface-0)' }}>
      <OrbisSpinner size={48} />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'var(--surface-0)', position: 'relative', overflow: 'hidden' }}>
      {/* ── Aurora background ── */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, opacity: 0.08, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, filter: 'blur(120px)' }}>
          <div className="orbis-aurora-1" />
          <div className="orbis-aurora-2" />
        </div>
      </div>
      <style>{`
        .orbis-aurora-1, .orbis-aurora-2 { position: absolute; border-radius: 50%; }
        .orbis-aurora-1 { width: 500px; height: 500px; background: var(--orbis-blue); top: 5%; left: 15%; animation: orbisAurora1 22s infinite alternate ease-in-out; }
        .orbis-aurora-2 { width: 400px; height: 400px; background: var(--orbis-amber); bottom: 15%; right: 10%; animation: orbisAurora2 28s infinite alternate ease-in-out; }
        @keyframes orbisAurora1 { from { transform: translate(0,0) rotate(0deg); } to { transform: translate(80px,40px) rotate(180deg); } }
        @keyframes orbisAurora2 { from { transform: translate(0,0) rotate(0deg); } to { transform: translate(-60px,-30px) rotate(-180deg); } }
      `}</style>

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 flex items-center justify-between" style={{ height: 64, padding: '0 20px', background: 'rgba(9,9,11,0.85)', backdropFilter: 'blur(16px)', borderBottom: '0.5px solid var(--orbis-s2)' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer" style={{ background: 'none', border: 'none', padding: 0 }}>
            <OrbisMarkSVG size={22} />
            <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>Orbis</span>
          </button>
          <span style={{ fontSize: 14, color: 'var(--orbis-t4)' }}>/</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>Billing & Credits</span>
        </div>
        <div className="flex items-center gap-3">
          <CreditMeter />
          <UserMenu />
        </div>
      </header>

      <main style={{ maxWidth: 960, margin: '0 auto', padding: '48px 24px', position: 'relative', zIndex: 1 }}>

        {/* ══════════════ PLANS SECTION ══════════════ */}
        <div style={{ marginBottom: 48 }} className="flex flex-col items-center">
          {/* Badge */}
          <motion.div
            custom={0} variants={fadeIn} initial="hidden" animate="visible"
            className="inline-flex items-center gap-2"
            style={{
              padding: '6px 14px', borderRadius: 100, marginBottom: 16,
              background: 'rgba(184,204,223,0.06)', border: '0.5px solid rgba(184,204,223,0.15)',
            }}
          >
            <Zap className="w-3.5 h-3.5" style={{ color: 'var(--orbis-blue)' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>
              Flexible & Transparent Pricing
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            custom={0.1} variants={fadeIn} initial="hidden" animate="visible"
            style={{ fontSize: 36, fontWeight: 700, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', letterSpacing: '-0.02em', marginBottom: 10, textAlign: 'center' }}
          >
            Find the Perfect Plan
          </motion.h1>

          <motion.p
            custom={0.2} variants={fadeIn} initial="hidden" animate="visible"
            style={{ fontSize: 14, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', marginBottom: 28, textAlign: 'center' }}
          >
            Start free. Upgrade to get the capacity that matches your needs.
          </motion.p>

          {/* ── Billing Toggle ── */}
          <motion.div
            custom={0.3} variants={fadeIn} initial="hidden" animate="visible"
            className="flex items-center justify-center gap-4" style={{ marginBottom: 36 }}
          >
            <span style={{ fontSize: 14, fontWeight: isAnnual ? 400 : 600, color: isAnnual ? 'var(--orbis-t4)' : 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', transition: 'all 0.2s' }}>
              Monthly
            </span>
            <div
              onClick={() => setIsAnnual(!isAnnual)}
              className="cursor-pointer"
              style={{
                width: 52, height: 28, borderRadius: 14, padding: 3,
                background: isAnnual ? 'var(--orbis-blue)' : 'var(--orbis-s3)',
                transition: 'background 0.2s', display: 'flex', alignItems: 'center',
              }}
            >
              <motion.div
                layout
                transition={{ type: 'spring', stiffness: 700, damping: 30 }}
                style={{
                  width: 22, height: 22, borderRadius: '50%', background: '#fff',
                  marginLeft: isAnnual ? 'auto' : '0',
                }}
              />
            </div>
            <span style={{ fontSize: 14, fontWeight: isAnnual ? 600 : 400, color: isAnnual ? 'var(--orbis-t1)' : 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', transition: 'all 0.2s' }}>
              Yearly
            </span>
            <AnimatePresence>
              {isAnnual && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                  style={{
                    fontSize: 11, fontFamily: 'var(--orbis-mono)', fontWeight: 600,
                    padding: '4px 10px', borderRadius: 100,
                    background: 'rgba(52, 211, 153, 0.12)', color: 'var(--orbis-positive)',
                  }}
                >
                  Save 20%
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ── 3 Plan Cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full" style={{ alignItems: 'start' }}>
            {PLANS.map((plan, index) => {
              const isCurrent = tier === plan.id;
              const planIdx = tierOrder.indexOf(plan.id);
              const isUpgrade = planIdx > currentTierIdx;
              const selCredits = selectedCredits[plan.id] || plan.defaultCredits;

              let displayPrice = plan.monthlyPrice;
              if (plan.creditOptions) {
                const opt = plan.creditOptions.find(o => o.credits === selCredits) || plan.creditOptions[0];
                displayPrice = isAnnual ? opt.annualPrice : opt.monthlyPrice;
              } else {
                displayPrice = isAnnual ? plan.annualPrice : plan.monthlyPrice;
              }

              return (
                <motion.div
                  key={plan.id}
                  custom={index}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  style={{
                    background: plan.highlight ? 'rgba(24,24,27,0.9)' : 'rgba(24,24,27,0.5)',
                    backdropFilter: 'blur(12px)',
                    border: plan.highlight ? '1px solid rgba(184,204,223,0.3)' : '0.5px solid var(--orbis-s2)',
                    borderRadius: 16,
                    padding: '28px 24px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Card aurora glow (visible on hover) */}
                  <div
                    className="card-glow"
                    style={{
                      position: 'absolute', inset: 0, opacity: 0,
                      background: plan.highlight
                        ? 'radial-gradient(circle at 50% 0%, rgba(184,204,223,0.15) 0%, transparent 60%)'
                        : 'radial-gradient(circle at 50% 0%, rgba(184,204,223,0.06) 0%, transparent 60%)',
                      transition: 'opacity 0.4s ease',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Badge */}
                  {plan.badge && (
                    <div style={{
                      position: 'absolute', top: 0, right: 0,
                      fontSize: 9, fontFamily: 'var(--orbis-mono)', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      padding: '5px 14px', borderBottomLeftRadius: 12,
                      background: 'var(--orbis-blue)', color: '#09090B',
                    }}>
                      {plan.badge}
                    </div>
                  )}

                  {/* Plan name + description */}
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', marginBottom: 6 }}>
                      {plan.name}
                    </h3>
                    <p style={{ fontSize: 13, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', marginBottom: 20, minHeight: 36, lineHeight: 1.5 }}>
                      {plan.description}
                    </p>

                    {/* Price — animated on toggle */}
                    {displayPrice !== null ? (
                      <>
                        <div className="flex items-baseline gap-1.5" style={{ marginBottom: 2 }}>
                          <span style={{ fontSize: 44, fontWeight: 800, fontFamily: 'var(--orbis-font)', color: 'var(--orbis-t1)', lineHeight: 1, letterSpacing: '-0.02em' }}>
                            <AnimatePresence mode="wait">
                              <motion.span
                                key={`${plan.id}-${isAnnual}-${selCredits}`}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.25 }}
                              >
                                ${displayPrice}
                              </motion.span>
                            </AnimatePresence>
                          </span>
                          <span style={{ fontSize: 14, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>
                            {plan.id === 'free' ? '' : '/mo'}
                          </span>
                        </div>
                        {plan.id !== 'free' && plan.id !== 'enterprise' && isAnnual && (
                          <p style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginBottom: 4 }}>
                            billed annually
                          </p>
                        )}
                      </>
                    ) : (
                      <div style={{ marginBottom: 6 }}>
                        <p style={{ fontSize: 24, fontWeight: 800, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', lineHeight: 1.3 }}>
                          Flexible billing
                        </p>
                      </div>
                    )}

                    {/* Annual toggle per card */}
                    {plan.id !== 'free' && plan.id !== 'enterprise' && (
                      <div className="flex items-center gap-2" style={{ marginTop: 10, marginBottom: 18 }}>
                        <div
                          onClick={() => setIsAnnual(!isAnnual)}
                          className="cursor-pointer"
                          style={{
                            width: 36, height: 18, borderRadius: 9, padding: 2,
                            background: isAnnual ? 'var(--orbis-positive)' : 'var(--orbis-s3)',
                            transition: 'background 0.2s', display: 'flex', alignItems: 'center',
                          }}
                        >
                          <motion.div
                            layout
                            transition={{ type: 'spring', stiffness: 700, damping: 30 }}
                            style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', marginLeft: isAnnual ? 'auto' : '0' }}
                          />
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>Annual</span>
                      </div>
                    )}

                    {/* CTA */}
                    {plan.id === 'enterprise' ? (
                      <button
                        onClick={() => toast.info('Contact us at hello@orbis.dev')}
                        className="cursor-pointer transition-all duration-200 hover:opacity-90"
                        style={{
                          width: '100%', padding: '11px', borderRadius: 10, marginBottom: 16,
                          background: 'transparent', border: '1px solid var(--orbis-s3)',
                          color: 'var(--orbis-t1)', fontSize: 14, fontWeight: 600,
                          fontFamily: 'var(--orbis-font)',
                        }}
                      >
                        {plan.cta}
                      </button>
                    ) : isCurrent ? (
                      <button disabled style={{
                        width: '100%', padding: '11px', borderRadius: 10, marginBottom: 16,
                        background: 'var(--orbis-s2)', border: 'none',
                        color: 'var(--orbis-t3)', fontSize: 14, fontWeight: 600,
                        fontFamily: 'var(--orbis-font)', cursor: 'default',
                      }}>
                        Current Plan
                      </button>
                    ) : isUpgrade ? (
                      <button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={upgrading !== null}
                        className="cursor-pointer transition-all duration-200 hover:opacity-90"
                        style={{
                          width: '100%', padding: '11px', borderRadius: 10, marginBottom: 16,
                          background: plan.highlight ? 'var(--orbis-blue)' : 'transparent',
                          border: plan.highlight ? 'none' : '1px solid var(--orbis-s3)',
                          color: plan.highlight ? '#09090B' : 'var(--orbis-t1)',
                          fontSize: 14, fontWeight: 600, fontFamily: 'var(--orbis-font)',
                        }}
                      >
                        {upgrading === plan.id ? <OrbisSpinner size={16} color={plan.highlight ? '#09090B' : 'var(--orbis-blue)'} /> : plan.cta}
                      </button>
                    ) : (
                      <button
                        onClick={handleManageSubscription}
                        className="cursor-pointer transition-all duration-200 hover:opacity-90"
                        style={{
                          width: '100%', padding: '11px', borderRadius: 10, marginBottom: 16,
                          background: 'transparent', border: '1px solid var(--orbis-s3)',
                          color: 'var(--orbis-t3)', fontSize: 14, fontWeight: 500,
                          fontFamily: 'var(--orbis-font)',
                        }}
                      >
                        Downgrade
                      </button>
                    )}

                    {/* Credit Selector */}
                    {plan.creditOptions && (
                      <select
                        value={selCredits}
                        onChange={e => setSelectedCredits(prev => ({ ...prev, [plan.id]: parseInt(e.target.value) }))}
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: 10, marginBottom: 18,
                          background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)',
                          color: 'var(--orbis-t1)', fontSize: 13, fontFamily: 'var(--orbis-font)',
                          appearance: 'auto', cursor: 'pointer',
                        }}
                      >
                        {plan.creditOptions.map(opt => (
                          <option key={opt.credits} value={opt.credits}>{opt.credits} credits / month</option>
                        ))}
                      </select>
                    )}

                    {/* Feature list */}
                    <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)', marginBottom: 12 }}>
                      {plan.featureHeader}
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {plan.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-2.5" style={{ marginBottom: 10 }}>
                          <CheckCircle className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-blue)', marginTop: 2 }} />
                          <span style={{ fontSize: 13, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)', lineHeight: 1.4 }}>
                            {feat.replace('{credits}', String(selCredits))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Manage + Compare buttons */}
          <motion.div custom={0.7} variants={fadeIn} initial="hidden" animate="visible"
            className="flex items-center justify-center gap-4" style={{ marginTop: 24 }}>
            {tier !== 'free' && (
              <button onClick={handleManageSubscription}
                className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                style={{ background: 'transparent', border: '0.5px solid var(--orbis-s3)', borderRadius: 100, padding: '8px 20px', color: 'var(--orbis-t3)', fontSize: 12, fontFamily: 'var(--orbis-font)' }}>
                Manage on Stripe <ExternalLink className="w-3 h-3" />
              </button>
            )}
            <button onClick={() => setShowCompare(!showCompare)}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              style={{ background: 'transparent', border: '0.5px solid var(--orbis-s3)', borderRadius: 100, padding: '8px 20px', color: 'var(--orbis-t3)', fontSize: 12, fontFamily: 'var(--orbis-font)' }}>
              {showCompare ? 'Hide comparison' : 'Compare all features'}
            </button>
          </motion.div>
        </div>

        {/* ══════════════ COMPARISON TABLE ══════════════ */}
        <AnimatePresence>
          {showCompare && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{ marginBottom: 40, overflow: 'hidden' }}
            >
              <div style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', borderRadius: 14, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '0.5px solid var(--orbis-s2)' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', width: '40%' }} />
                      {PLANS.map(p => (
                        <th key={p.id} style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 600, fontFamily: 'var(--orbis-font)', color: tier === p.id ? 'var(--orbis-blue)' : 'var(--orbis-t2)', background: tier === p.id ? 'rgba(184,204,223,0.04)' : 'transparent' }}>
                          {p.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map((row) => {
                      if (row.category) return (
                        <tr key={`cat-${row.category}`} style={{ background: 'var(--orbis-s2)' }}>
                          <td colSpan={5} style={{ padding: '6px 16px', fontSize: 10, fontFamily: 'var(--orbis-mono)', fontWeight: 600, color: 'var(--orbis-t4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{row.category}</td>
                        </tr>
                      );
                      return (
                        <tr key={row.label} style={{ borderBottom: '0.5px solid var(--orbis-s2)' }}>
                          <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>{row.label}</td>
                          {['free', 'pro', 'business', 'enterprise'].map(pid => {
                            const val = row[pid]; const isCur = tier === pid;
                            return (
                              <td key={pid} style={{ padding: '10px 16px', textAlign: 'center', fontSize: 13, fontFamily: typeof val === 'boolean' ? 'var(--orbis-font)' : 'var(--orbis-mono)', fontWeight: 500, background: isCur ? 'rgba(184,204,223,0.04)' : 'transparent' }}>
                                {typeof val === 'boolean' ? (val ? <Check className="w-4 h-4 inline-block" style={{ color: 'var(--orbis-positive)' }} /> : <Minus className="w-4 h-4 inline-block" style={{ color: 'var(--orbis-t4)' }} />) : <span style={{ color: val === 'Unlimited' ? 'var(--orbis-positive)' : 'var(--orbis-t1)' }}>{val}</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ══════════════ BALANCE CARD ══════════════ */}
        <div style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', borderRadius: 14, padding: 28, marginBottom: 32 }}>
          <div className="flex items-start justify-between">
            <div>
              <p style={{ fontSize: 11, fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Available Credits</p>
              <div className="flex items-baseline gap-3">
                <span style={{ fontSize: 40, fontFamily: 'var(--orbis-mono)', fontWeight: 600, color: 'var(--orbis-t1)', lineHeight: 1 }}>{balance}</span>
                <span style={{ fontSize: 10, fontFamily: 'var(--orbis-mono)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', padding: '4px 12px', borderRadius: 100, border: `0.5px solid ${tierColor}40`, color: tierColor, background: `${tierColor}10` }}>{tierLabel}</span>
              </div>
              <p style={{ fontSize: 12, color: 'var(--orbis-t4)', marginTop: 8, fontFamily: 'var(--orbis-font)' }}>{lifetimeUsed} credits used lifetime</p>
            </div>
            <Coins className="w-7 h-7" style={{ color: 'var(--orbis-t4)', opacity: 0.2 }} />
          </div>
          {monthlyCredits > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="flex justify-between" style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', marginBottom: 4 }}>
                <span>{monthlyCredits - balance} used</span><span>{monthlyCredits} monthly</span>
              </div>
              <div style={{ height: 3, borderRadius: 2, background: 'var(--orbis-s2)', overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 2, width: `${usagePercent}%`, background: usagePercent > 80 ? '#F59E0B' : 'var(--orbis-blue)', transition: 'width 0.3s ease' }} />
              </div>
            </div>
          )}
        </div>

        {/* ══════════════ CREDIT COSTS ══════════════ */}
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)', marginBottom: 16 }}>Credit Costs</h2>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(costs).map(([action, cost]) => {
              const meta = ACTION_META[action] || { icon: Zap, label: action, color: 'var(--orbis-t3)' };
              const Icon = meta.icon;
              return (
                <div key={action} className="flex items-center justify-between" style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', borderRadius: 10, padding: '10px 14px' }}>
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    <span style={{ fontSize: 13, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>{meta.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontFamily: 'var(--orbis-mono)', fontWeight: 600, color: cost === 0 ? 'var(--orbis-positive)' : 'var(--orbis-t1)' }}>
                    {cost === 0 ? 'Free' : `${cost} cr`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ══════════════ ADD CREDITS ══════════════ */}
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)', marginBottom: 16 }}>Add Credits</h2>
          <div className="grid grid-cols-3 gap-3">
            {CREDIT_PACKS.map(pack => (
              <button key={pack.amount} onClick={() => handlePurchase(pack.amount)} disabled={purchasing !== null}
                className="cursor-pointer hover:border-[var(--orbis-blue)] transition-colors"
                style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', borderRadius: 12, padding: '20px 16px', textAlign: 'center' }}>
                {purchasing === pack.amount ? <OrbisSpinner size={20} color="var(--orbis-blue)" /> : (
                  <>
                    <div style={{ fontSize: 24, fontFamily: 'var(--orbis-mono)', fontWeight: 600, color: 'var(--orbis-t1)', marginBottom: 4 }}>+{pack.amount}</div>
                    <div style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>{pack.price}</div>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ══════════════ TRANSACTION HISTORY ══════════════ */}
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)', marginBottom: 16 }}>Transaction History</h2>
          <div style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', borderRadius: 14, overflow: 'hidden' }}>
            {historyLoading ? (
              <div className="flex justify-center py-8"><OrbisSpinner size={24} /></div>
            ) : history.length === 0 ? (
              <div className="py-8 text-center" style={{ color: 'var(--orbis-t4)', fontSize: 13 }}>No transactions yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid var(--orbis-s2)' }}>
                    {['Date', 'Action', 'Credits', 'Balance'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', fontSize: 10, fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 500, textAlign: h === 'Credits' || h === 'Balance' ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map(tx => {
                    const meta = ACTION_META[tx.type] || { icon: Zap, label: tx.type, color: 'var(--orbis-t3)' };
                    const Icon = meta.icon;
                    const isPositive = tx.amount > 0;
                    return (
                      <tr key={tx.id} style={{ borderBottom: '0.5px solid var(--orbis-s2)' }}>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)' }}>
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5" style={{ color: meta.color }} /><span style={{ fontSize: 13, color: 'var(--orbis-t2)' }}>{meta.label}</span></div>
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 13, fontFamily: 'var(--orbis-mono)', fontWeight: 600, color: isPositive ? 'var(--orbis-positive)' : '#F87171', textAlign: 'right' }}>
                          {isPositive ? '+' : ''}{tx.amount}
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 12, fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t3)', textAlign: 'right' }}>{tx.balance_after}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {/* Hover glow CSS */}
      <style>{`
        [class*="card-glow"] { pointer-events: none; }
        .grid > div:hover .card-glow { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
